import { useCallback, useEffect, useRef } from 'react';
import { ApiService } from 'api/services/ApiService';
import { clientWithAuth } from 'dashboard409a/services/base';
import { DROPBOX_FOLDER_STATUS } from './dropbox.constants';
import type { DropboxEntry, DropboxFolderOption } from './dropbox.types';

type DropboxCreateFolderResult = typeof DROPBOX_FOLDER_STATUS.CREATED | typeof DROPBOX_FOLDER_STATUS.EXISTS;
const DROPBOX_OAUTH_STATE_KEY = 'dropbox_oauth_state';
const DROPBOX_OAUTH_RETURN_TO_KEY = 'dropbox_oauth_return_to';

const getErrorText = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message.toLowerCase();
  }
  try {
    return JSON.stringify(error).toLowerCase();
  } catch {
    return String(error).toLowerCase();
  }
};

const isDropboxNotFoundError = (error: unknown): boolean => {
  const errorText = getErrorText(error);
  return errorText.includes('not_found') || errorText.includes('path/not_found');
};

const isDropboxConflictError = (error: unknown): boolean => {
  const errorText = getErrorText(error);
  return errorText.includes('conflict') || errorText.includes('path/conflict');
};

const getDropboxRedirectUri = (): string => {
  const currentUrl = new URL(globalThis.window.location.href);
  return `${currentUrl.origin}${currentUrl.pathname}`;
};

const removeOAuthParamsFromUrl = (url: URL): URL => {
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  url.searchParams.delete('error');
  return url;
};

const extractDropboxFolderOptions = (payload: unknown): DropboxFolderOption[] => {
  const optionsByPath = new Map<string, DropboxFolderOption>();
  const namesOnly = new Set<string>();

  const addOption = (name: unknown, pathDisplay: unknown) => {
    if (typeof name !== 'string') {
      return;
    }
    const normalizedName = name.trim();
    if (!normalizedName) {
      return;
    }
    namesOnly.add(normalizedName);
    if (typeof pathDisplay === 'string' && pathDisplay.trim()) {
      const normalizedPath = pathDisplay.trim();
      if (!optionsByPath.has(normalizedPath)) {
        optionsByPath.set(normalizedPath, {
          name: normalizedName,
          pathDisplay: normalizedPath,
        });
      }
    }
  };

  const visit = (node: unknown): void => {
    if (!node || typeof node !== 'object') {
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }

    const nodeRecord = node as Record<string, unknown>;
    const tag = nodeRecord['.tag'];
    const { name } = nodeRecord;
    if (tag === 'folder') {
      addOption(name, nodeRecord.path_display);
    }

    const nestedMetadata = nodeRecord.metadata;
    if (nestedMetadata && typeof nestedMetadata === 'object') {
      const nestedRecord = nestedMetadata as Record<string, unknown>;
      if (nestedRecord['.tag'] === 'folder') {
        addOption(nestedRecord.name, nestedRecord.path_display);
      }
      const doubleNestedMetadata = nestedRecord.metadata;
      if (doubleNestedMetadata && typeof doubleNestedMetadata === 'object') {
        const doubleNestedRecord = doubleNestedMetadata as Record<string, unknown>;
        if (doubleNestedRecord['.tag'] === 'folder') {
          addOption(doubleNestedRecord.name, doubleNestedRecord.path_display);
        }
      }
    }

    Object.values(nodeRecord).forEach(visit);
  };

  const root = payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)
    ? (payload as Record<string, unknown>).data
    : payload;

  visit(root);

  const existingNames = new Set(Array.from(optionsByPath.values()).map(option => option.name.toLowerCase()));
  namesOnly.forEach(name => {
    if (!existingNames.has(name.toLowerCase())) {
      optionsByPath.set(`name-only:${name}`, { name, pathDisplay: '' });
    }
  });

  return Array.from(optionsByPath.values()).filter(option => Boolean(option.pathDisplay));
};

const useDropbox = () => {
  const callbackInFlightRef = useRef<Promise<void> | null>(null);
  const lastHandledCodeRef = useRef<string | null>(null);

  const startOAuth = useCallback(async (): Promise<void> => {
    const currentUrl = removeOAuthParamsFromUrl(new URL(globalThis.window.location.href));
    const oauthState = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(DROPBOX_OAUTH_STATE_KEY, oauthState);
    localStorage.setItem(DROPBOX_OAUTH_RETURN_TO_KEY, currentUrl.toString());
    const redirectUri = getDropboxRedirectUri();

    const response = await clientWithAuth.get('/api/dropbox/oauth2/authorize-url', {
      params: {
        redirect_uri: redirectUri,
        state: oauthState,
      },
    });
    const authorizeUrl = response?.data?.authorize_url;
    if (!authorizeUrl || typeof authorizeUrl !== 'string') {
      throw new Error('Failed to initialize Dropbox authorization');
    }

    globalThis.window.location.assign(authorizeUrl);
  }, []);

  const handleOAuthCallbackIfPresent = useCallback(async (): Promise<void> => {
    if (callbackInFlightRef.current) {
      return callbackInFlightRef.current;
    }

    const callbackRun = (async () => {
      const currentUrl = new URL(globalThis.window.location.href);
      const code = currentUrl.searchParams.get('code');
      const state = currentUrl.searchParams.get('state');
      const oauthError = currentUrl.searchParams.get('error');
      const expectedState = localStorage.getItem(DROPBOX_OAUTH_STATE_KEY);

      if (oauthError) {
        const cleanedUrl = removeOAuthParamsFromUrl(new URL(globalThis.window.location.href));
        globalThis.window.history.replaceState({}, document.title, `${cleanedUrl.pathname}${cleanedUrl.search}${cleanedUrl.hash}`);
        localStorage.removeItem(DROPBOX_OAUTH_STATE_KEY);
        localStorage.removeItem(DROPBOX_OAUTH_RETURN_TO_KEY);
        throw new Error(`Dropbox OAuth failed: ${oauthError}`);
      }

      if (!code || lastHandledCodeRef.current === code) {
        return;
      }


      if (expectedState && expectedState !== state) {
        const cleanedUrl = removeOAuthParamsFromUrl(new URL(globalThis.window.location.href));
        globalThis.window.history.replaceState({}, document.title, `${cleanedUrl.pathname}${cleanedUrl.search}${cleanedUrl.hash}`);
        localStorage.removeItem(DROPBOX_OAUTH_STATE_KEY);
        localStorage.removeItem(DROPBOX_OAUTH_RETURN_TO_KEY);
        throw new Error('Dropbox OAuth state mismatch');
      }

      try {
        await clientWithAuth.post('/api/dropbox/oauth2/token', {
          grant_type: 'authorization_code',
          code,
          redirect_uri: `${currentUrl.origin}${currentUrl.pathname}`,
        });

        lastHandledCodeRef.current = code;
        const returnTo = localStorage.getItem(DROPBOX_OAUTH_RETURN_TO_KEY);
        const nextUrl = returnTo
          ? removeOAuthParamsFromUrl(new URL(returnTo, currentUrl.origin))
          : removeOAuthParamsFromUrl(new URL(globalThis.window.location.href));

        globalThis.window.history.replaceState({}, document.title, `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
        localStorage.removeItem(DROPBOX_OAUTH_STATE_KEY);
        localStorage.removeItem(DROPBOX_OAUTH_RETURN_TO_KEY);
      } catch (error) {
        const cleanedUrl = removeOAuthParamsFromUrl(new URL(globalThis.window.location.href));
        globalThis.window.history.replaceState({}, document.title, `${cleanedUrl.pathname}${cleanedUrl.search}${cleanedUrl.hash}`);
        localStorage.removeItem(DROPBOX_OAUTH_STATE_KEY);
        localStorage.removeItem(DROPBOX_OAUTH_RETURN_TO_KEY);
        throw error;
      }
    })().finally(() => {
      callbackInFlightRef.current = null;
    });

    callbackInFlightRef.current = callbackRun;
    return callbackRun;
  }, []);

  useEffect(() => {
    const run = async () => {
      try {
        await handleOAuthCallbackIfPresent();
      } catch {
        // surfaced by user-triggered actions
      }
    };
    run();
  }, [handleOAuthCallbackIfPresent]);

  const checkIsAuthenticated = useCallback(async (): Promise<boolean> => {
    await handleOAuthCallbackIfPresent();
    const response = await clientWithAuth.get('/api/dropbox/oauth2/status');
    return Boolean(response?.data?.connected);
  }, [handleOAuthCallbackIfPresent]);

  const ensureAuthenticated = useCallback(async (): Promise<boolean> => {
    const connected = await checkIsAuthenticated();
    if (connected) {
      return true;
    }

    await startOAuth();
    return false;
  }, [checkIsAuthenticated, startOAuth]);

  const checkFolderExists = useCallback(async (path: string): Promise<boolean> => {
    const isAuthenticated = await ensureAuthenticated();
    if (!isAuthenticated) {
      throw new Error('Dropbox authentication required');
    }

    try {
      await ApiService.apiDropboxCreate('files/get_metadata', { path });
      return true;
    } catch (error) {
      if (isDropboxNotFoundError(error)) {
        return false;
      }
      throw error;
    }
  }, [ensureAuthenticated]);

  const searchFolders = useCallback(async (query: string, scopePath: string): Promise<DropboxFolderOption[]> => {
    const isAuthenticated = await ensureAuthenticated();
    if (!isAuthenticated) {
      throw new Error('Dropbox authentication required');
    }

    if (query.trim()) {
      const response = await ApiService.apiDropboxCreate('files/search_v2', {
        query: query.trim(),
        options: {
          path: scopePath,
          max_results: 20,
          filename_only: true,
          file_categories: [
            {
              '.tag': 'folder'
            }
          ]
        },
      });
      return extractDropboxFolderOptions(response);
    }

    const response = await ApiService.apiDropboxCreate('files/list_folder', {
      path: scopePath,
      recursive: false,
      include_mounted_folders: true,
    });
    return extractDropboxFolderOptions(response);
  }, [ensureAuthenticated]);

  const listFolder = useCallback(async (path: string): Promise<DropboxFolderOption[]> => {
    const isAuthenticated = await ensureAuthenticated();
    if (!isAuthenticated) {
      throw new Error('Dropbox authentication required');
    }

    try {
      const response = await ApiService.apiDropboxCreate('files/list_folder', {
        path,
        recursive: false,
        include_media_info: false,
        include_non_downloadable_files: false,
      });

      const entries = response.data?.entries
        ?.filter((entry: DropboxEntry) => entry['.tag'] === 'folder')
        .map((entry: DropboxEntry) => ({
          name: entry.name,
          pathDisplay: entry.path_display ?? '',
        }));
      return entries || [];
    } catch (error) {
      if (isDropboxNotFoundError(error)) {
        return [];
      }
      throw error;
    }
  }, [ensureAuthenticated]);

  const createFolder = useCallback(async (path: string): Promise<DropboxCreateFolderResult> => {
    const isAuthenticated = await ensureAuthenticated();
    if (!isAuthenticated) {
      throw new Error('Dropbox authentication required');
    }

    try {
      await ApiService.apiDropboxCreate('files/create_folder_v2', {
        path,
        autorename: false,
      });
      return DROPBOX_FOLDER_STATUS.CREATED;
    } catch (error) {
      if (isDropboxConflictError(error)) {
        return DROPBOX_FOLDER_STATUS.EXISTS;
      }
      throw error;
    }
  }, [ensureAuthenticated]);

  return {
    ensureAuthenticated,
    checkIsAuthenticated,
    connectDropbox: startOAuth,
    checkFolderExists,
    searchFolders,
    createFolder,
    listFolder,
  };
};

export default useDropbox;